import { useEffect, useMemo, useState } from 'react';
import Modal from './Modal';
import PosApplicationReviewModal from './PosApplicationReviewModal';
import { useConfig } from '../context/ConfigContext';
import { spinnerRing } from '../utils/bookingUtils';
import { formatMoney } from '../utils/currencyUtil';
import { subscribeAllApplications } from '../utils/posSubscriptionUtil';
import { POS_APPLICATION_STATUS } from '../constants/posConst';
import '../css/PosAdmin.css';
import '../css/PosSubscription.css';

/**
 * The side panel on the Clients screen: subscription applications waiting for
 * review, oldest first — the one that has waited longest is the one to do next.
 *
 * @param {object} props
 * @param {string} props.signedInEmail - recorded as the reviewer
 * @returns {JSX.Element}
 */
function PosPendingApplications({ signedInEmail }) {
  const { config } = useConfig();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => subscribeAllApplications(
    (list) => { setApplications(list); setLoading(false); setLoadError(null); },
    (error) => { setLoadError(error); setLoading(false); },
  ), []);

  const pending = useMemo(() => applications
    .filter((application) => application.status === POS_APPLICATION_STATUS.PENDING)
    .sort((a, b) => (a.submittedAt || 0) - (b.submittedAt || 0)), [applications]);

  return (
    <aside className="ps-panel" aria-label="Pending subscription applications">
      <header className="ps-panel-head">
        <h2 className="ps-panel-title">Pending applications</h2>
        {!loading && !loadError && <span className="ps-count">{pending.length}</span>}
      </header>

      {loading ? (
        <div className="pa-loading pa-loading-inline">{spinnerRing('Loading')}</div>
      ) : loadError ? (
        <p className="pa-field-hint">Applications could not be read: {loadError.message}</p>
      ) : pending.length === 0 ? (
        <p className="ps-panel-empty">Nothing waiting. New subscription applications from clients will show up here.</p>
      ) : (
        <ul className="ps-app-list">
          {pending.map((application) => (
            <li key={`${application.clientId}-${application.id}`}>
              <button type="button" className="ps-app" onClick={() => setReviewing(application)}>
                <span className="ps-app-top">
                  <span className="ps-app-client">{application.clientName}</span>
                  <span className="ps-app-total">{formatMoney(application.total, config)}</span>
                </span>
                <span className="ps-app-meta">
                  {application.planName}
                  {(application.extraStaffEmails || []).length > 0
                    ? ` + ${application.extraStaffEmails.length} staff`
                    : ''}
                </span>
                <span className="ps-app-meta">{new Date(application.submittedAt).toLocaleString()}</span>
                <span className="ps-app-cta">Review</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <PosApplicationReviewModal
        application={reviewing}
        config={config}
        signedInEmail={signedInEmail}
        onClose={() => setReviewing(null)}
        onDone={(outcome) => { setReviewing(null); setResult(outcome); }}
      />

      <Modal
        isOpen={Boolean(result)}
        onClose={() => setResult(null)}
        title={result?.title || ''}
        message={result?.message || ''}
        type={result?.type || 'info'}
      />
    </aside>
  );
}

export default PosPendingApplications;
